/**
 * `api/media.ts` — typed access to WordPress Media items.
 *
 * This module fronts the WP REST `/wp/v2/media/{id}` and
 * `/wp/v2/media?include={ids}` endpoints. Callers use it to hydrate a
 * page's or post's `featured_media` id into a fully-typed `Media`
 * object with the full responsive-size manifest (`hb-320`, `hb-640`,
 * `hb-960`, `hb-1280`, `hb-1920`, plus core `thumbnail`/`medium`/
 * `large`) surfaced under `mediaDetails.sizes`. That manifest is what
 * `frontend/src/lib/srcset.ts` and `<ResponsiveImage />` consume to
 * emit `srcset`/`sizes` markup (Req 8.2).
 *
 * Design contract:
 * - Uses the shared `apiClient` from `./client`; never constructs an
 *   Axios instance of its own (Req 4.5, Req 16.6).
 * - Only shape returned to callers is the camelCase, strongly-typed
 *   `Media` interface — snake_case WP keys never leak past this
 *   module (Req 4.2).
 * - Handles partial / malformed responses defensively. WordPress may
 *   return items where `media_details.sizes` is `[]` (empty array
 *   masquerading as an object) or missing entirely for freshly-
 *   uploaded files; every normalizer below returns sensible defaults
 *   in those cases so the UI never crashes on incomplete data.
 *
 * Requirements: 2.5, 4.1, 4.2, 8.1, 8.2.
 */

import { apiClient } from './client';
import type { Media, MediaSize } from '@/types';

/**
 * Fetch a single Media item by its numeric WordPress id.
 *
 * @param id - WordPress attachment id (as returned by `page.featured_media`
 *             or `post.featured_media`).
 * @returns   Fully normalized `Media` object with all available responsive
 *            size variants under `mediaDetails.sizes`.
 * @throws    `ApiError` on any non-2xx response, timeout, or network
 *            failure (see `./errors.ts`).
 *
 * Requirement mapping: 2.5, 4.1, 4.2, 8.1.
 */
export async function getMedia(id: number): Promise<Media> {
  const safeId = encodeURIComponent(String(id));
  const { data } = await apiClient.get<unknown>(`/wp-json/wp/v2/media/${safeId}`);
  return normalizeMedia(data);
}

/**
 * Fetch multiple Media items in a single request using WP's `include`
 * query parameter, then return them in the same order as the input
 * `ids` when possible.
 *
 * WordPress accepts `orderby=include` to preserve the include-array
 * order, but not every WP version / plugin stack honors it, so we
 * also reorder locally to guarantee the contract. Any items that WP
 * returns which are NOT in the requested `ids` list are appended at
 * the end (defensive — should not happen in practice).
 *
 * An empty `ids` array short-circuits to `[]` without a network call
 * so callers do not need to guard against it.
 *
 * @param ids - Attachment ids to fetch. Order is preserved in the
 *              returned array whenever WP returns the requested id.
 * @returns    Array of normalized `Media` objects. May be shorter than
 *             `ids` if some ids were missing / trashed on the CMS.
 * @throws     `ApiError` on any non-2xx response, timeout, or network
 *             failure (see `./errors.ts`).
 *
 * Requirement mapping: 2.5, 4.1, 4.2, 8.1, 8.2.
 */
export async function listMedia(ids: readonly number[]): Promise<Media[]> {
  if (ids.length === 0) {
    return [];
  }

  const { data } = await apiClient.get<unknown>('/wp-json/wp/v2/media', {
    params: {
      include: ids.join(','),
      per_page: ids.length,
      orderby: 'include',
    },
  });

  const rawArray: readonly unknown[] = Array.isArray(data) ? data : [];
  const items = rawArray.map((raw) => normalizeMedia(raw));

  // Reorder to the caller-provided `ids` sequence. WP may return a
  // different order (e.g. when `orderby=include` is unsupported) or
  // omit items that no longer exist.
  const byId = new Map<number, Media>();
  for (const item of items) {
    if (item.id > 0) {
      byId.set(item.id, item);
    }
  }

  const ordered: Media[] = [];
  const consumed = new Set<number>();
  for (const wantedId of ids) {
    const found = byId.get(wantedId);
    if (found !== undefined && !consumed.has(wantedId)) {
      ordered.push(found);
      consumed.add(wantedId);
    }
  }

  // Defensive: preserve any Media items WP returned that were not in
  // the requested set (should not happen for `include=` queries, but
  // keeps the function total).
  for (const item of items) {
    if (item.id > 0 && !consumed.has(item.id)) {
      ordered.push(item);
      consumed.add(item.id);
    }
  }

  return ordered;
}

/**
 * Normalize a raw WP Media response (snake_case, permissive shape)
 * into the strongly-typed camelCase `Media` interface consumed by the
 * rest of the Frontend.
 *
 * Missing or malformed fields are replaced with defaults so components
 * never have to guard against `undefined` on a hydrated `Media` value:
 *
 * - `id`: coerced to int; `0` when absent.
 * - `sourceUrl`, `altText`, `mimeType`: coerced to string; `''` when absent.
 * - `caption`: pulled from `caption.rendered`; `''` when absent.
 * - `mediaType`: `'image'` when raw value is `'image'`, otherwise `'file'`.
 * - `mediaDetails.sizes`: each entry normalized via `normalizeSize`,
 *   using the outer `mimeType` as the mime fallback so per-size entries
 *   inherit the parent's mime when WP omits it (common for `hb-*` sizes).
 *
 * Requirement mapping: 2.5, 4.2, 8.2.
 */
export function normalizeMedia(raw: unknown): Media {
  if (!isRecord(raw)) {
    return emptyMedia();
  }

  const id = toInt(raw.id);
  const sourceUrl = toStr(raw.source_url);
  const altText = toStr(raw.alt_text);
  const caption = isRecord(raw.caption) ? toStr(raw.caption.rendered) : '';
  const mediaType = coerceMediaType(raw.media_type);
  const mimeType = toStr(raw.mime_type);

  const detailsRecord = isRecord(raw.media_details) ? raw.media_details : {};
  const width = toInt(detailsRecord.width);
  const height = toInt(detailsRecord.height);

  // WP occasionally serializes an empty sizes map as `[]` instead of
  // `{}` (PHP associative-array quirk). Treat non-object values as
  // "no sizes present" rather than crashing.
  const sizesRecord = isRecord(detailsRecord.sizes) ? detailsRecord.sizes : {};
  const sizes: Record<string, MediaSize> = {};
  for (const [key, value] of Object.entries(sizesRecord)) {
    sizes[key] = normalizeSize(value, mimeType);
  }

  return {
    id,
    sourceUrl,
    altText,
    caption,
    mediaType,
    mimeType,
    mediaDetails: {
      width,
      height,
      sizes,
    },
  };
}

/**
 * Normalize a single entry from `media_details.sizes` into a typed
 * `MediaSize`. Any missing field is replaced with a default (`''` for
 * strings, `0` for numeric dimensions). `mimeFallback` is used when
 * the size entry omits `mime_type`, which is common for the custom
 * `hb-*` sizes registered by the `headless-bridge` plugin.
 *
 * Requirement mapping: 2.5, 4.2, 8.2.
 */
export function normalizeSize(raw: unknown, mimeFallback?: string): MediaSize {
  const fallbackMime = mimeFallback ?? '';
  if (!isRecord(raw)) {
    return {
      file: '',
      width: 0,
      height: 0,
      sourceUrl: '',
      mimeType: fallbackMime,
    };
  }
  const rawMime = toStr(raw.mime_type);
  return {
    file: toStr(raw.file),
    width: toInt(raw.width),
    height: toInt(raw.height),
    sourceUrl: toStr(raw.source_url),
    mimeType: rawMime.length > 0 ? rawMime : fallbackMime,
  };
}

// ---------------------------------------------------------------------------
// Internal helpers (not exported).
// ---------------------------------------------------------------------------

function emptyMedia(): Media {
  return {
    id: 0,
    sourceUrl: '',
    altText: '',
    caption: '',
    mediaType: 'file',
    mimeType: '',
    mediaDetails: {
      width: 0,
      height: 0,
      sizes: {},
    },
  };
}

function coerceMediaType(value: unknown): Media['mediaType'] {
  return value === 'image' ? 'image' : 'file';
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}

function toStr(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

/**
 * Coerce an arbitrary value to a finite non-negative integer.
 *
 * Accepts numeric strings (WP sometimes stringifies ids in embedded
 * responses). Returns `0` for anything that cannot be coerced.
 */
function toInt(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.trunc(value);
  }
  if (typeof value === 'string' && value.trim() !== '') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) {
      return Math.trunc(parsed);
    }
  }
  return 0;
}
